import { promptDialog } from '../../../../../profint.shared10/src/Profint.Shared.UI/assets/feedback';
import '../../../../../profint.shared10/src/Profint.Shared.UI/wwwroot/js/shared-temporal-controls.js';
type SharedTemporalApi = {
  parseTime(value: string): { hour: number; minute: number } | null;
  validateRange(from: string, to: string, options: { kind: string; requireFrom: boolean; requireTo: boolean; allowEqual: boolean }): { valid: boolean };
};
const sharedTemporal = () => (window as unknown as { ProfintTemporal: SharedTemporalApi }).ProfintTemporal;
type AgendaPreset = 'weekdays' | 'monday-saturday' | 'all';
type TimeRange = { desde: string; hasta: string; cupoMaximo?: number | null };
type DayOverride = {
  fecha: string;
  bloqueado: boolean;
  cupoMaximo?: number | null;
  motivo?: string | null;
  bloqueosHorarios?: TimeRange[] | null;
};
type DaySchedule = { dia: string; rangos: TimeRange[]; cupoEspontanea?: number };

const EDITOR_SELECTOR = '[data-agenda-editor]';
const DAY_ORDER = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const WEEK_DAY_FALLBACK = 'Lunes';
const DAY_LABELS: Record<string, string> = {
  lunes: 'Lunes',
  monday: 'Lunes',
  martes: 'Martes',
  tuesday: 'Martes',
  miércoles: 'Miércoles',
  miercoles: 'Miércoles',
  wednesday: 'Miércoles',
  jueves: 'Jueves',
  thursday: 'Jueves',
  viernes: 'Viernes',
  friday: 'Viernes',
  sábado: 'Sábado',
  sabado: 'Sábado',
  saturday: 'Sábado',
  domingo: 'Domingo',
  sunday: 'Domingo',
};
const DAY_PRESETS: Record<AgendaPreset, string[]> = {
  weekdays: DAY_ORDER.slice(0, 5),
  'monday-saturday': DAY_ORDER.slice(0, 6),
  all: [...DAY_ORDER],
};
const TIME_PRESETS: Record<string, [string, string]> = {
  morning: ['08:00', '12:00'],
  'full-day': ['08:00', '18:00'],
  afternoon: ['14:00', '20:00'],
};
const MONTH_FORMATTER = new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric' });
const DAY_KEY_FORMATTER = new Intl.DateTimeFormat('en-CA');
const DATE_FORMATTER = new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
function argentinaToday(): Date {
  const key = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());
  return new Date(`${key}T12:00:00`);
}

function dateKey(date: Date): string {
  return DAY_KEY_FORMATTER.format(date);
}

function parseJson<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function dayLabel(value: string | null | undefined): string {
  const key = (value || '').trim().toLowerCase();
  return DAY_LABELS[key] || value || WEEK_DAY_FALLBACK;
}

function normalizeScheduleDays(schedule: DaySchedule[]): DaySchedule[] {
  const merged = new Map<string, DaySchedule>();
  schedule.forEach(item => {
    const dia = dayLabel(item.dia);
    const current = merged.get(dia);
    if (!current) {
      merged.set(dia, { ...item, dia, rangos: [...(item.rangos || [])] });
      return;
    }
    current.cupoEspontanea = Math.max(current.cupoEspontanea || 0, item.cupoEspontanea || 0);
    current.rangos.push(...(item.rangos || []));
    current.rangos.sort((a, b) => a.desde.localeCompare(b.desde));
  });
  return Array.from(merged.values()).sort((a, b) => DAY_ORDER.indexOf(a.dia) - DAY_ORDER.indexOf(b.dia));
}

function timeToMinutes(value: string): number | null {
  // Agenda's payload is minute precision. Keep that contract over the shared parser.
  if (!/^\d{2}:\d{2}$/.test(value)) return null;
  const parsed = sharedTemporal().parseTime(value);
  return parsed ? parsed.hour * 60 + parsed.minute : null;
}

function selectedDays(editor: HTMLElement): string[] {
  return Array.from(editor.querySelectorAll<HTMLButtonElement>('[data-day-value].is-selected'))
    .map(button => button.dataset.dayValue || '')
    .filter(Boolean);
}

function setSelectedDays(editor: HTMLElement, days: string[]): void {
  const selected = new Set(days);
  editor.querySelectorAll<HTMLButtonElement>('[data-day-value]').forEach(button => {
    const isSelected = selected.has(button.dataset.dayValue || '');
    button.classList.toggle('is-selected', isSelected);
    button.setAttribute('aria-pressed', String(isSelected));
  });
}

function updatePresetState(editor: HTMLElement): void {
  const days = selectedDays(editor);
  const matches = (preset: string[]) => preset.length === days.length
    && preset.every((day, index) => day === days[index]);

  editor.querySelectorAll<HTMLButtonElement>('[data-days-preset]').forEach(button => {
    const preset = button.dataset.daysPreset as AgendaPreset | undefined;
    button.classList.toggle('is-active', !!preset && matches(DAY_PRESETS[preset]));
  });

  const from = editor.querySelector<HTMLInputElement>('[data-agenda-time="from"]')?.value;
  const to = editor.querySelector<HTMLInputElement>('[data-agenda-time="to"]')?.value;
  editor.querySelectorAll<HTMLButtonElement>('[data-time-preset]').forEach(button => {
    const preset = TIME_PRESETS[button.dataset.timePreset || ''];
    button.classList.toggle('is-active', !!preset && preset[0] === from && preset[1] === to);
  });
}

function daysSummary(days: string[]): string {
  if (days.length === 0) return 'Sin días seleccionados';
  if (days.length === DAY_PRESETS.weekdays.length && DAY_PRESETS.weekdays.every((day, index) => day === days[index])) {
    return 'Lunes a viernes';
  }
  if (days.length === DAY_PRESETS['monday-saturday'].length && DAY_PRESETS['monday-saturday'].every((day, index) => day === days[index])) {
    return 'Lunes a sábado';
  }
  if (days.length === DAY_ORDER.length) return 'Todos los días';
  return days.join(' · ');
}

function updateEditor(editor: HTMLElement): boolean {
  const days = selectedDays(editor);
  const hiddenDays = editor.querySelector<HTMLInputElement>('[data-days-value]');
  const daysSummaryElement = editor.querySelector<HTMLElement>('[data-days-summary]');
  const daysError = editor.querySelector<HTMLElement>('[data-days-error]');
  const from = editor.querySelector<HTMLInputElement>('[data-agenda-time="from"]');
  const to = editor.querySelector<HTMLInputElement>('[data-agenda-time="to"]');
  const duration = editor.querySelector<HTMLInputElement>('[data-agenda-duration]');
  const capacity = editor.querySelector<HTMLInputElement>('[data-agenda-capacity]');
  const timeError = editor.querySelector<HTMLElement>('[data-time-error]');

  if (hiddenDays) hiddenDays.value = days.join(',');
  if (daysSummaryElement) {
    daysSummaryElement.innerHTML = `<i class="ph ph-check-circle" aria-hidden="true"></i><span>${daysSummary(days)}</span>`;
  }

  const fromMinutes = timeToMinutes(from?.value || '');
  const toMinutes = timeToMinutes(to?.value || '');
  const durationMinutes = Number(duration?.value || 0);
  const dailyCapacity = Number(capacity?.value || 0);
  const validTimeRange = fromMinutes !== null && toMinutes !== null && sharedTemporal().validateRange(from?.value || '', to?.value || '', { kind: 'time', requireFrom: true, requireTo: true, allowEqual: false }).valid;
  const validDuration = Number.isInteger(durationMinutes) && durationMinutes >= 5 && durationMinutes <= 480;
  const validCapacity = Number.isInteger(dailyCapacity) && dailyCapacity >= 1 && dailyCapacity <= 500;
  const validDays = days.length > 0;
  const valid = validDays && validTimeRange && validDuration && validCapacity;

  if (daysError) daysError.textContent = validDays ? '' : 'Seleccioná al menos un día de atención.';
  if (timeError) timeError.textContent = validTimeRange ? '' : 'El horario de cierre debe ser posterior al de inicio.';

  if (from) from.setCustomValidity(validTimeRange ? '' : 'Definí un rango horario válido.');
  if (to) to.setCustomValidity(validTimeRange ? '' : 'El cierre debe ser posterior al inicio.');
  if (duration) duration.setCustomValidity(validDuration ? '' : 'La duración debe estar entre 5 y 480 minutos.');
  if (capacity) capacity.setCustomValidity(validCapacity ? '' : 'El cupo debe estar entre 1 y 500 turnos.');

  const preview = editor.querySelector<HTMLElement>('[data-agenda-preview]');
  const previewTitle = editor.querySelector<HTMLElement>('[data-preview-title]');
  const previewDetail = editor.querySelector<HTMLElement>('[data-preview-detail]');
  const previewCapacity = editor.querySelector<HTMLElement>('[data-preview-capacity]');
  const slotsByRange = validTimeRange && validDuration
    ? Math.floor((toMinutes! - fromMinutes!) / durationMinutes)
    : 0;
  const slotsPerDay = validCapacity ? Math.min(slotsByRange, dailyCapacity) : 0;

  if (preview) preview.classList.toggle('is-invalid', !valid);
  if (previewTitle) previewTitle.textContent = daysSummary(days);
  if (previewDetail) {
    previewDetail.textContent = validTimeRange && validDuration
      ? `${from!.value} a ${to!.value} · turnos de ${durationMinutes} min · máximo ${dailyCapacity} por día`
      : 'Completá el rango horario y la duración para ver el resumen.';
  }
  if (previewCapacity) previewCapacity.textContent = validTimeRange && validDuration && validCapacity
    ? String(slotsPerDay)
    : '—';

  updatePresetState(editor);
  syncSimpleSchedule(editor);
  return valid;
}

function initEditor(editor: HTMLElement): void {
  if (editor.dataset.agendaEditorReady === 'true') return;
  editor.dataset.agendaEditorReady = 'true';
  initAgendaTabs(editor);
  initCalendar(editor);
  initWeekSchedule(editor);

  const daysPicker = editor.querySelector<HTMLElement>('[data-days-picker]');
  daysPicker?.addEventListener('click', event => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('[data-day-value]')
      : null;
    if (!target) return;

    target.classList.toggle('is-selected');
    target.setAttribute('aria-pressed', String(target.classList.contains('is-selected')));
    updateEditor(editor);
  });

  editor.querySelectorAll<HTMLButtonElement>('[data-days-preset]').forEach(button => {
    button.addEventListener('click', () => {
      const preset = button.dataset.daysPreset as AgendaPreset | undefined;
      if (!preset) return;
      setSelectedDays(editor, DAY_PRESETS[preset]);
      updateEditor(editor);
    });
  });

  editor.querySelectorAll<HTMLButtonElement>('[data-time-preset]').forEach(button => {
    button.addEventListener('click', () => {
      const preset = TIME_PRESETS[button.dataset.timePreset || ''];
      if (!preset) return;
      const from = editor.querySelector<HTMLInputElement>('[data-agenda-time="from"]');
      const to = editor.querySelector<HTMLInputElement>('[data-agenda-time="to"]');
      if (from) from.value = preset[0];
      if (to) to.value = preset[1];
      updateEditor(editor);
    });
  });

  editor.querySelectorAll<HTMLInputElement>('[data-agenda-time], [data-agenda-duration], [data-agenda-capacity]')
    .forEach(input => input.addEventListener('input', () => updateEditor(editor)));

  editor.addEventListener('submit', event => {
    if (updateEditor(editor)) return;
    event.preventDefault();
    const invalid = editor.querySelector<HTMLElement>('.is-invalid, :invalid');
    invalid?.focus({ preventScroll: false });
  });

  updateEditor(editor);
}

function syncSimpleSchedule(editor: HTMLElement): void {
  const input = editor.querySelector<HTMLInputElement>('[data-week-schedule-json]');
  const list = editor.querySelector<HTMLElement>('[data-week-list]');
  if (!input || !list || list.childElementCount > 0) return;
  const from = editor.querySelector<HTMLInputElement>('[data-agenda-time="from"]')?.value;
  const to = editor.querySelector<HTMLInputElement>('[data-agenda-time="to"]')?.value;
  const capacity = Number(editor.querySelector<HTMLInputElement>('[data-agenda-capacity]')?.value || 0);
  if (!from || !to || capacity <= 0) return;
  input.value = JSON.stringify(selectedDays(editor).map(dia => ({
    dia,
    cupoEspontanea: 0,
    rangos: [{ desde: from, hasta: to, cupoMaximo: capacity }],
  })));
}

function normalizeTime(value: string): string {
  return value.slice(0, 5);
}

function initWeekSchedule(editor: HTMLElement): void {
  const root = editor.querySelector<HTMLElement>('[data-week-schedule]');
  const input = editor.querySelector<HTMLInputElement>('[data-week-schedule-json]');
  const list = editor.querySelector<HTMLElement>('[data-week-list]');
  if (!root || !input || !list) return;
  const from = root.querySelector<HTMLInputElement>('[data-week-from]');
  const to = root.querySelector<HTMLInputElement>('[data-week-to]');
  const walkin = root.querySelector<HTMLInputElement>('[data-week-walkin]');
  if (!from || !to || !walkin) return;

  let schedule = normalizeScheduleDays(parseJson<DaySchedule[]>(input.value, []));
  let selectedDay = root.querySelector<HTMLButtonElement>('[data-week-day].is-active')?.dataset.weekDay || WEEK_DAY_FALLBACK;
  const sync = (): void => {
    input.value = JSON.stringify(schedule.filter(day => selectedDays(editor).includes(day.dia)));
  };
  editor.addEventListener('submit', sync);
  const render = (): void => {
    list.innerHTML = schedule.length === 0
      ? '<span class="lic-week-schedule__empty">Se usará la regla semanal simple.</span>'
      : '';
    schedule.forEach((item, dayIndex) => {
      const group = document.createElement('div');
      group.className = 'lic-week-schedule__day';
      group.innerHTML = `<strong>${item.dia}</strong><small>${item.cupoEspontanea || 0} espontáneos</small>`;
      item.rangos.forEach((range, rangeIndex) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.dataset.weekRemove = `${dayIndex}:${rangeIndex}`;
        chip.innerHTML = `${normalizeTime(range.desde)}-${normalizeTime(range.hasta)} <i class="ph ph-x"></i>`;
        group.appendChild(chip);
      });
      list.appendChild(group);
    });
  };
  editor.querySelector('[data-week-apply-simple]')?.addEventListener('click', () => {
    const start = editor.querySelector<HTMLInputElement>('[data-agenda-time="from"]')?.value || '';
    const end = editor.querySelector<HTMLInputElement>('[data-agenda-time="to"]')?.value || '';
    if (timeToMinutes(start) === null || timeToMinutes(end) === null || end <= start) return;
    schedule = selectedDays(editor).map(dia => ({ dia, rangos: [{ desde: start, hasta: end }], cupoEspontanea: 0 }));
    sync(); render();
  });
  root.querySelector('[data-week-add]')?.addEventListener('click', () => {
    if (!from.value || !to.value || to.value <= from.value) {
      to.reportValidity();
      return;
    }
    let item = schedule.find(x => x.dia === selectedDay);
    if (!item) {
      item = { dia: selectedDay, cupoEspontanea: 0, rangos: [] };
      schedule.push(item);
    }
    item.cupoEspontanea = Number(walkin.value || item.cupoEspontanea || 0);
    item.rangos.push({ desde: from.value, hasta: to.value });
    item.rangos.sort((a, b) => a.desde.localeCompare(b.desde));
    sync();
    render();
  });
  root.querySelector<HTMLElement>('[data-week-day-picker]')?.addEventListener('click', event => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('[data-week-day]')
      : null;
    if (!target?.dataset.weekDay) return;
    selectedDay = target.dataset.weekDay;
    root.querySelectorAll<HTMLButtonElement>('[data-week-day]').forEach(button => {
      const active = button === target;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  });
  list.addEventListener('click', event => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('[data-week-remove]')
      : null;
    if (!target?.dataset.weekRemove) return;
    const [dayIndex, rangeIndex] = target.dataset.weekRemove.split(':').map(Number);
    schedule[dayIndex]?.rangos.splice(rangeIndex, 1);
    schedule = schedule.filter(item => item.rangos.length > 0);
    sync();
    render();
  });
  render();
  sync();
}

function initAgendaTabs(editor: HTMLElement): void {
  const tabs = Array.from(editor.querySelectorAll<HTMLButtonElement>('[data-agenda-tab]'));
  const panels = Array.from(editor.querySelectorAll<HTMLElement>('[data-agenda-panel]'));
  const activate = (target: string): void => {
    tabs.forEach(item => {
      const active = item.dataset.agendaTab === target;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', String(active));
    });
    panels.forEach(panel => {
      const active = panel.dataset.agendaPanel === target;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
  };
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      activate(tab.dataset.agendaTab || 'weekly');
    });
  });
  activate(tabs.find(tab => tab.classList.contains('is-active'))?.dataset.agendaTab || 'weekly');
}

function initCalendar(editor: HTMLElement): void {
  const calendar = editor.querySelector<HTMLElement>('[data-agenda-calendar]');
  if (!calendar) return;

  const input = editor.querySelector<HTMLInputElement>('[data-calendar-overrides-json]');
  const title = calendar.querySelector<HTMLElement>('[data-calendar-title]');
  const grid = calendar.querySelector<HTMLElement>('[data-calendar-grid]');
  const menu = calendar.querySelector<HTMLElement>('[data-calendar-menu]');
  const selectedTitle = calendar.querySelector<HTMLElement>('[data-calendar-selected-title]');
  const selectedSummary = calendar.querySelector<HTMLElement>('[data-calendar-selected-summary]');
  const selectedStatus = calendar.querySelector<HTMLElement>('[data-calendar-selected-status]');
  const selectedNote = calendar.querySelector<HTMLElement>('[data-calendar-selected-note]');
  if (!input || !title || !grid || !menu || !selectedTitle || !selectedSummary || !selectedStatus || !selectedNote) return;

  const state = {
    month: argentinaToday(),
    view: 'month',
    selected: dateKey(argentinaToday()),
    overrides: new Map<string, DayOverride>(
      parseJson<DayOverride[]>(input.value, []).map(item => [item.fecha, item]),
    ),
  };
  const holidays = new Map(parseJson<{ fecha: string; descripcion?: string }[]>(editor.dataset.agendaHolidays, []).map(day => [day.fecha, day.descripcion || 'Feriado / día no laborable']));
  const sync = (): void => {
    input.value = JSON.stringify(Array.from(state.overrides.values()).sort((a, b) => a.fecha.localeCompare(b.fecha)));
  };
  const currentOverride = (): DayOverride => state.overrides.get(state.selected) || {
    fecha: state.selected,
    bloqueado: false,
    cupoMaximo: null,
    motivo: null,
    bloqueosHorarios: [],
  };
  const setOverride = (value: DayOverride): void => {
    state.overrides.set(value.fecha, value);
    sync();
    render();
  };
  const askReason = async (fallback: string): Promise<string | null> => {
    const value = await promptDialog({ title: 'Motivo del cambio', value: fallback, maxLength: 1000 });
    return value === null ? null : value.trim() || fallback;
  };
  const render = (): void => {
    const weekly = state.view === 'week';
    const list = state.view === 'list';
    grid.classList.toggle('is-list', list);
    grid.classList.toggle('is-week', weekly);
    title.textContent = MONTH_FORMATTER.format(state.month);
    grid.innerHTML = '';
    if (!list) ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].forEach(day => {
      const head = document.createElement('span');
      head.className = 'lic-agenda-calendar__head';
      head.textContent = day;
      grid.appendChild(head);
    });
    const cursor = new Date(state.month);
    if (!weekly) cursor.setDate(1);
    if (!list) cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
    const count = weekly ? 7 : list ? new Date(state.month.getFullYear(), state.month.getMonth() + 1, 0).getDate() : 42;
    if (weekly) {
      const end = new Date(cursor); end.setDate(end.getDate() + 6);
      title.textContent = `${DATE_FORMATTER.format(cursor)} al ${DATE_FORMATTER.format(end)}`;
    }
    const schedule = normalizeScheduleDays(parseJson<DaySchedule[]>(editor.querySelector<HTMLInputElement>('[data-week-schedule-json]')?.value, []));
    for (let index = 0; index < count; index += 1) {
      const key = dateKey(cursor);
      const item = state.overrides.get(key);
      const holiday = holidays.get(key);
      const day = DAY_ORDER[(cursor.getDay() + 6) % 7];
      const ranges = schedule.find(row => row.dia === day)?.rangos || [];
      const agendaFrom = (editor.dataset.agendaFrom || '').slice(0, 10);
      const agendaUntil = (editor.dataset.agendaUntil || '').slice(0, 10);
      const outside = (!!agendaFrom && key < agendaFrom) || (!!agendaUntil && key > agendaUntil);
      const closed = !!holiday || !!item?.bloqueado || !selectedDays(editor).includes(day) || ranges.length === 0;
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.calendarDate = key;
      button.className = [
        'lic-agenda-calendar__day',
        (!weekly && cursor.getMonth() !== state.month.getMonth()) || outside ? 'is-muted' : '',
        key === state.selected ? 'is-selected' : '',
        closed ? 'is-blocked' : '',
        item && !item.bloqueado && !closed ? 'has-override' : '',
      ].filter(Boolean).join(' ');
      const heading = document.createElement('strong');
      heading.textContent = list ? `${day} ${DATE_FORMATTER.format(cursor)}` : String(cursor.getDate());
      const detail = document.createElement('span');
      detail.textContent = holiday ? `Feriado: ${holiday}` : item?.bloqueado ? `Bloqueado: ${item.motivo || 'Cierre operativo'}`
        : closed ? 'Sin atención' : outside ? 'Fuera del período de reserva'
        : `${normalizeTime(ranges[0].desde)}–${normalizeTime(ranges[ranges.length - 1].hasta)} · ${ranges.length} ${ranges.length === 1 ? 'franja' : 'franjas'}`;
      button.append(heading, detail);
      if (!closed && !outside) {
        const capacity = document.createElement('small');
        capacity.textContent = `Cupo ${item?.cupoMaximo || editor.querySelector<HTMLInputElement>('[data-agenda-capacity]')?.value || '—'}${item?.bloqueosHorarios?.length ? ` · ${item.bloqueosHorarios.length} ${item.bloqueosHorarios.length === 1 ? 'bloqueo' : 'bloqueos'} horarios` : ''}`;
        button.appendChild(capacity);
      }
      button.setAttribute('aria-label', `${day} ${DATE_FORMATTER.format(cursor)}. ${detail.textContent}`);
      button.setAttribute('aria-pressed', String(key === state.selected));
      grid.appendChild(button);
      cursor.setDate(cursor.getDate() + 1);
    }

    const [selectedYear, selectedMonth, selectedDayOfMonth] = state.selected.split('-').map(Number);
    const selectedDate = new Date(selectedYear, selectedMonth - 1, selectedDayOfMonth, 12);
    const selectedKey = state.selected;
    const selectedWeekday = DAY_ORDER[(selectedDate.getDay() + 6) % 7];
    const selectedOverride = state.overrides.get(selectedKey);
    const selectedHoliday = holidays.get(selectedKey);
    const selectedRanges = schedule.find(row => row.dia === selectedWeekday)?.rangos || [];
    const agendaFrom = (editor.dataset.agendaFrom || '').slice(0, 10);
    const agendaUntil = (editor.dataset.agendaUntil || '').slice(0, 10);
    const selectedOutside = (!!agendaFrom && selectedKey < agendaFrom) || (!!agendaUntil && selectedKey > agendaUntil);
    const selectedClosed = !!selectedHoliday || !!selectedOverride?.bloqueado
      || !selectedDays(editor).includes(selectedWeekday) || selectedRanges.length === 0;
    const summary = selectedHoliday ? `Feriado: ${selectedHoliday}. Este día permanece cerrado.`
      : selectedOverride?.bloqueado ? selectedOverride.motivo || 'Día bloqueado para reservas.'
      : selectedClosed ? 'Sin atención según la regla semanal.'
      : selectedRanges.map(range => `${normalizeTime(range.desde)}–${normalizeTime(range.hasta)}`).join(' · ');
    selectedTitle.textContent = `${selectedWeekday} ${DATE_FORMATTER.format(selectedDate)}`;
    selectedSummary.textContent = summary;
    selectedStatus.textContent = selectedHoliday ? 'Feriado · cerrado'
      : selectedOverride?.bloqueado ? 'Bloqueado'
      : selectedOutside ? 'Fuera de la ventana de reserva'
      : selectedClosed ? 'Sin atención'
      : 'Agenda habilitada';
    selectedStatus.className = `lic-calendar-day__status${selectedHoliday || selectedOverride?.bloqueado ? ' is-closed' : ''}`;
    const blockedRanges = selectedOverride?.bloqueosHorarios || [];
    const capacity = selectedOverride?.cupoMaximo || editor.querySelector<HTMLInputElement>('[data-agenda-capacity]')?.value || '—';
    selectedNote.textContent = selectedHoliday ? 'Los feriados del catálogo no admiten cambios desde esta agenda.'
      : `${selectedRanges.length} ${selectedRanges.length === 1 ? 'franja semanal' : 'franjas semanales'} · Cupo máximo ${capacity}${blockedRanges.length ? ` · ${blockedRanges.length} ${blockedRanges.length === 1 ? 'horario bloqueado' : 'horarios bloqueados'}` : ''}${selectedOverride?.motivo && !selectedOverride.bloqueado ? ` · ${selectedOverride.motivo}` : ''}`;
    menu.querySelectorAll<HTMLButtonElement>('[data-calendar-action]').forEach(action => {
      action.disabled = !!selectedHoliday || (action.dataset.calendarAction === 'clear' && !selectedOverride);
    });
  };
  const navigate = (direction: number): void => {
    if (state.view === 'week') state.month.setDate(state.month.getDate() + direction * 7);
    else { state.month.setDate(1); state.month.setMonth(state.month.getMonth() + direction); }
    state.selected = dateKey(state.month);
    render();
  };
  calendar.querySelector('[data-calendar-prev]')?.addEventListener('click', () => navigate(-1));
  calendar.querySelector('[data-calendar-next]')?.addEventListener('click', () => navigate(1));
  calendar.querySelector('[data-calendar-today]')?.addEventListener('click', () => {
    state.month = argentinaToday();
    state.selected = dateKey(state.month);
    render();
  });
  calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-view]').forEach(button => button.addEventListener('click', () => {
    state.view = button.dataset.calendarView || 'month';
    calendar.querySelectorAll<HTMLButtonElement>('[data-calendar-view]').forEach(tab => {
      tab.classList.toggle('is-active', tab === button); tab.setAttribute('aria-pressed', String(tab === button));
    });
    render();
  }));
  editor.querySelector('[data-agenda-tab="calendar"]')?.addEventListener('click', render);

  grid.addEventListener('click', event => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('[data-calendar-date]')
      : null;
    if (!target?.dataset.calendarDate) return;
    state.selected = target.dataset.calendarDate;
    render();
    menu.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  });
  grid.addEventListener('contextmenu', event => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('[data-calendar-date]')
      : null;
    if (!target?.dataset.calendarDate) return;
    event.preventDefault();
    state.selected = target.dataset.calendarDate;
    render();
    menu.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  });
  menu.addEventListener('click', async event => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>('[data-calendar-action]')
      : null;
    if (!target?.dataset.calendarAction || holidays.has(state.selected)) return;
    const value = currentOverride();
    const action = target.dataset.calendarAction;
    if (action === 'block') {
      const motivo = await askReason(value.motivo || 'Bloqueo operativo');
      if (motivo === null) return;
      setOverride({ ...value, bloqueado: true, cupoMaximo: null, motivo });
    }
    if (action === 'capacity') {
      const entered = await promptDialog({ title: 'Cupo online para este día', value: value.cupoMaximo ? String(value.cupoMaximo) : '', inputMode: 'numeric', required: true, validate: text => /^\d+$/.test(text.trim()) && Number(text) >= 1 && Number(text) <= 500 ? null : 'Ingresá un cupo entero entre 1 y 500.' });
      if (entered === null) return;
      const cupo = Number(entered);
      if (!Number.isInteger(cupo) || cupo < 1 || cupo > 500) return;
      const motivo = await askReason(value.motivo || 'Cupo excepcional');
      if (motivo === null) return;
      setOverride({ ...value, bloqueado: false, cupoMaximo: cupo, motivo });
    }
    if (action === 'time') {
      const desde = await promptDialog({ title: 'Bloquear desde', placeholder: 'HH:mm', value: '12:00', required: true, validate: text => /^([01]\d|2[0-3]):[0-5]\d$/.test(text) ? null : 'Ingresá un horario válido: HH:mm.' });
      if (desde === null) return;
      const hasta = await promptDialog({ title: 'Bloquear hasta', placeholder: 'HH:mm', value: '13:00', required: true, validate: text => /^([01]\d|2[0-3]):[0-5]\d$/.test(text) && text > desde ? null : 'Ingresá HH:mm posterior al inicio.' });
      if (hasta === null || timeToMinutes(desde) === null || timeToMinutes(hasta) === null || hasta <= desde) return;
      const motivo = await askReason(value.motivo || 'Bloqueo horario');
      if (motivo === null) return;
      setOverride({
        ...value,
        bloqueado: false,
        motivo,
        bloqueosHorarios: [...(value.bloqueosHorarios || []), { desde, hasta }],
      });
    }
    if (action === 'clear') {
      state.overrides.delete(state.selected);
      sync();
      render();
    }
  });
  render();
  sync();
}

export function initLicenciasAgendaEditors(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>(EDITOR_SELECTOR).forEach(initEditor);
}

function initialize(): void {
  initLicenciasAgendaEditors();
  document.querySelectorAll<HTMLFormElement>('[data-availability-form]').forEach(form => {
    const summary = form.querySelector<HTMLElement>('[data-availability-summary]');
    const update = (): void => {
      if (!summary) return;
      const days = Number(form.querySelector<HTMLInputElement>('[name="AnticipacionMaximaDias"]')?.value || 0);
      const until = form.querySelector<HTMLInputElement>('[name="VigenciaHasta"]')?.value;
      const horizon = argentinaToday(); horizon.setDate(horizon.getDate() + days);
      const end = until ? new Date(until) : null;
      const limit = end && end < horizon ? end : horizon;
      summary.textContent = `${until ? `La oferta cierra el ${DATE_FORMATTER.format(end!)}.` : 'La oferta no tiene fecha de cierre.'} La ventana móvil de ${days} días permite reservar hasta el ${DATE_FORMATTER.format(limit)}, sujeto a agenda cargada y cupos libres. Los feriados permanecen cerrados. Horarios de Argentina.`;
    };
    form.addEventListener('input', update); form.addEventListener('change', update); update();
  });
  window.addEventListener('profint:navigation:ready', event => {
    const root = (event as CustomEvent<{ root?: ParentNode }>).detail?.root;
    initLicenciasAgendaEditors(root ?? document);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
